import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';

interface ConfirmDialogProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  /** When set, the confirm button stays disabled until this exact text is typed. */
  requireText?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A destructive-action confirmation. Native `<dialog>` with `showModal()`, like
 * `NewConversation`, so focus trap, backdrop and Esc come from the platform.
 * Mount it only while it should be open.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  requireText,
  busy = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const ready = !busy && (requireText === undefined || typed === requireText);

  return (
    <dialog
      ref={ref}
      className="modal modal--confirm"
      onClose={onCancel}
      aria-labelledby="confirm-title"
    >
      <header className="modal-head">
        <h2 id="confirm-title">{title}</h2>
        <button type="button" className="btn btn--icon" onClick={onCancel} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>
      <div className="modal-scroll">
        <div className="confirm-body">{children}</div>
        {requireText !== undefined ? (
          <div className="field">
            <label htmlFor="confirm-text">
              Type <strong>{requireText}</strong> to confirm
            </label>
            <input
              id="confirm-text"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        ) : null}
        {error ? (
          <p className="field-error" role="alert">
            <Icon name="alertCircle" size={13} />
            {error}
          </p>
        ) : null}
      </div>
      <footer className="modal-foot">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--danger-solid"
          disabled={!ready}
          onClick={onConfirm}
        >
          {busy ? <span className="btn-spinner" /> : <Icon name="trash" size={14} />}
          {confirmLabel}
        </button>
      </footer>
    </dialog>
  );
}
